import React, { useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useObserver } from 'mobx-react-lite';
import useUser from 'src/hooks/use-user';
import { useModals, ModalNames } from 'src/components/modals';

import { Typography } from 'src/typography';
import { SpecialOfferCard } from 'src/components/special-offer-card';
import { SpecialsSortDropdown, sortSpecials, useSpecialsSort } from 'src/components/specials-sort';
import useUI from 'src/hooks/use-ui';
import { Dispensaries, SpecialMenuCard } from 'src/gql/graphql';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';
import type { SpecialsPageHeaderProps } from '@dutchiesdk/ecommerce-extensions-sdk';

const SKELETON_CARD_COUNT = 24;

type OfferMenuSectionV2Props = {
  dispensary: Dispensaries;
  specials: SpecialMenuCard[];
  headerProps?: SpecialsPageHeaderProps;
  pageTitle?: string;
  pageDescription?: string;
  loading?: boolean;
  showDaysLeft?: boolean;
};

export function OfferMenuSectionV2({
  dispensary,
  specials,
  headerProps,
  pageTitle,
  pageDescription,
  loading = false,
  showDaysLeft = true,
}: OfferMenuSectionV2Props): JSX.Element | null {
  const { dispensaryRootUrl } = useUI();
  const { openModal } = useModals();
  const User = useUser();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const { sortKey, setSortKey } = useSpecialsSort();

  const resolvedHeaderProps: SpecialsPageHeaderProps = useMemo(
    () =>
      headerProps ?? {
        pageType: 'all-specials',
        defaultTitle: pageTitle ?? '',
      },
    [headerProps, pageTitle]
  );

  const NativeSpecialsPageHeader: React.FC<SpecialsPageHeaderProps> = useCallback(
    ({ defaultTitle }) => (
      <>
        <PageTitle size='medium' tag='h1'>
          {defaultTitle}
        </PageTitle>
        {pageDescription && <Label>{pageDescription}</Label>}
      </>
    ),
    [pageDescription]
  );

  const ResolvedSpecialsPageHeader = useRemoteComponent<SpecialsPageHeaderProps>(
    RemoteComponentSlot.SpecialsPageHeader,
    { fallback: NativeSpecialsPageHeader }
  );

  const sortedSpecials = useMemo(() => sortSpecials(specials, sortKey), [specials, sortKey]);
  const hasRestrictedSpecials = useMemo(() => specials.some((special) => special.isRestricted), [specials]);
  const showLogin = !isLoggedIn && hasRestrictedSpecials;

  const getSpecialUrl = (special: SpecialMenuCard): string | null => {
    const { _id, specialType } = special;
    if (!_id || !specialType) {
      return null;
    }
    const specialTypeSubRoute = specialType === `sale` ? specialType : `offer`;

    return `/${dispensaryRootUrl}/${dispensary.cName}/specials/${specialTypeSubRoute}/${_id}`;
  };

  const handleLoginClick = (): void => {
    void openModal(ModalNames.login);
  };

  if (loading) {
    return (
      <Section>
        <SectionHeader>
          <HeaderWrapper>
            <ResolvedSpecialsPageHeader {...resolvedHeaderProps} />
          </HeaderWrapper>
          <SpecialsSortDropdown activeSortKey={sortKey} onSortChange={setSortKey} />
        </SectionHeader>

        <SpecialsGridContainer
          role='status'
          aria-live='polite'
          aria-busy='true'
          aria-label='Loading specials'
          data-testid='offer-menu-section-v2-loading'
        >
          {Array.from({ length: SKELETON_CARD_COUNT }).map((_val, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonCard key={index} aria-hidden='true'>
              <SkeletonImage />
              <SkeletonText>
                <SkeletonLine $width='80%' />
                <SkeletonLine $width='55%' />
                <SkeletonLine $width='40%' />
              </SkeletonText>
            </SkeletonCard>
          ))}
        </SpecialsGridContainer>
      </Section>
    );
  }

  if (specials.length === 0) {
    return null;
  }

  return (
    <Section>
      <SectionHeader>
        <HeaderWrapper>
          <ResolvedSpecialsPageHeader {...resolvedHeaderProps} />
        </HeaderWrapper>
        <SpecialsSortDropdown activeSortKey={sortKey} onSortChange={setSortKey} />
      </SectionHeader>

      {showLogin && (
        <LoginPrompt>
          <LoginIcon>
            <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z'
                fill='currentColor'
              />
              <path
                d='M10 12C4.47715 12 0 14.4772 0 17.5C0 18.8807 1.11929 20 2.5 20H17.5C18.8807 20 20 18.8807 20 17.5C20 14.4772 15.5228 12 10 12Z'
                fill='currentColor'
              />
            </svg>
          </LoginIcon>
          <LoginText>Sign in to see exclusive member offers</LoginText>
          <LoginButton onClick={handleLoginClick} data-testid='specials-login-button'>
            Sign In
          </LoginButton>
        </LoginPrompt>
      )}

      <SpecialsGridContainer>
        {sortedSpecials.map((special) => {
          const specialUrl = getSpecialUrl(special);
          return specialUrl ? (
            <SpecialOfferCard key={special._id} special={special} href={specialUrl} showDaysLeft={showDaysLeft} />
          ) : null;
        })}
      </SpecialsGridContainer>
    </Section>
  );
}

const Label = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[35]};
  line-height: 30px;
  margin: 0 0 14px;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding-left: 25px;
    margin: 0;
  }
`;

// Column count is derived from available width rather than fixed breakpoints, so it never drops
// to fewer columns than actually fit. max-width caps the row at 3 cards, which also makes each
// track exactly 358px at full desktop width — the card's intended size.
// auto-fill (not auto-fit) is deliberate: auto-fit collapses empty tracks, which would stretch
// tracks well past 358px when a page has fewer specials than columns and leave the cards
// stranded with big gaps between them. auto-fill keeps the empty tracks so cards stay a
// consistent width and pack against the left edge, in line with the heading above.
// The min(280px, 100%) floor keeps the single column from overflowing very narrow viewports.
// Cards must drop their carousel styling's fixed 358px width and 234px height here (see the
// `> a` override) so a column narrower or wider than 358px is filled exactly rather than leaving
// dead space — and since the image's aspect-ratio grows with width, the height has to follow.
const SpecialsGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
  gap: 16px;
  margin: 20px 0;
  max-width: calc(358px * 3 + 16px * 2);
  width: 100%;

  > a {
    height: auto;
    max-width: none;
    min-width: 0;
    width: 100%;
  }
`;

const PageTitle = styled(Typography.Heading)`
  margin: 0;
`;

const HeaderWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const SectionHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Section = styled.div`
  padding: 0 16px;
`;

const LoginPrompt = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #f0f9f5 0%, #e3f3eb 100%);
  border: 1px solid #c7e5d6;
  border-radius: 8px;
  padding: 16px 20px;
  margin: 16px 0 24px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin: 16px 20px 24px;
    flex-wrap: wrap;
    justify-content: center;
    text-align: center;
  }
`;

const LoginIcon = styled.div`
  color: #00624a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LoginText = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #2e3438;
  line-height: 1.4;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    flex-basis: 100%;
    order: -1;
    margin-bottom: 8px;
  }
`;

const LoginButton = styled.button`
  background: #00624a;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  transition: background 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #004d3a;
  }

  &:active {
    background: #003d2e;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
  }
`;

const skeletonPulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const SkeletonCard = styled.div`
  align-items: center;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors?.greyBorder ?? '#e3e7e9'};
  border-radius: var(--dcm-radius-cards, 12px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 16px;
  width: 100%;
`;

const SkeletonImage = styled.div`
  animation: ${skeletonPulse} 1s linear infinite;
  aspect-ratio: 286 / 124;
  background-color: ${({ theme }) => theme.colors?.grey?.[95] ?? '#e3e7e9'};
  border-radius: 8px;
  width: 100%;
`;

const SkeletonText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;
`;

const SkeletonLine = styled.div<{ $width: string }>`
  animation: ${skeletonPulse} 1s linear infinite;
  background-color: ${({ theme }) => theme.colors?.grey?.[95] ?? '#e3e7e9'};
  border-radius: 4px;
  height: 14px;
  width: ${({ $width }) => $width};
`;
